import { ArrowBack, Lock } from '@mui/icons-material';
import { Box, Button, Chip, Container, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link, redirect, useNavigate, useViewTransitionState } from 'react-router';

import { ApiError, get, getOr } from '@/api/client';
import { RUTAS } from '@/api/rutas';
import { comoRespuesta } from '@/lib/errores';
import { CodigoDePieza } from '@/components/CodigoDePieza';
import { FichaDelProducto } from '@/components/FichaDelProducto';
import { GaleriaDeProducto } from '@/components/GaleriaDeProducto';
import { MedirProducto } from '@/components/MedirProducto';
import { enPesos, nombreDeMarca } from '@/lib/catalogo';
import { comoProducto, migasDePan, SITIO } from '@/lib/datos-estructurados';
import { direccionDelRubro, tieneRubro } from '@/lib/rubros';
import { direccionDeIngreso } from '@/lib/sesion';
import { useDatosDelSitio } from '@/root';
import type { Imagen, Producto, ResumenDeProducto } from '@/tipos';

import type { Route } from './+types/producto';

/** Cuántos relacionados se muestran, y cuántas fotos entran en la galería. */
const RELACIONADOS = 6;
const MAXIMO_DE_FOTOS = 10;

interface Respuesta {
  products: Producto[];
}

/**
 * La ficha sin sesión: lo que ven Google y la vista previa de WhatsApp.
 *
 * Código, nombre, rubro y marca. Ni fotos ni stock ni precios ni relacionados: eso es de quien
 * tiene cuenta. La ficha sigue indexada igual, porque es por donde entra quien busca una pieza
 * por su código.
 */
interface FichaSinSesion {
  anonima: true;
  producto: ResumenDeProducto;
}

interface FichaConSesion {
  anonima: false;
  producto: Producto;
  fotos: string[];
  relacionados: Producto[];
  fotosDeRelacionados: Record<string, string | null>;
}

export type DatosDeLaFicha = FichaSinSesion | FichaConSesion;

export function meta({ data, params }: Route.MetaArgs): Route.MetaDescriptors {
  return metaDeLaFicha(data, params.codigo);
}

/** Afuera de `meta` para poder probarla sin armar una ruta. */
export function metaDeLaFicha(
  data: DatosDeLaFicha | undefined,
  codigo: string,
): Route.MetaDescriptors {
  if (!data) {
    return [{ title: 'Producto no encontrado | CKING' }];
  }

  const { producto } = data;
  const titulo = `${producto.code} — ${producto.description} | CKING`;

  // El rubro entra en la descripción sólo si es un rubro. 130 productos caen en "SIN CATEGORIA",
  // que es el cajón de la planilla: publicarlo es contarle al mundo cómo ordenamos un Excel.
  const rubro = tieneRubro(producto.category) ? producto.category : null;

  // Sin sesión no se dice si hay stock: es justamente lo que se reserva para quien tiene cuenta.
  const disponibilidad = data.anonima
    ? 'En CKING'
    : `${data.producto.is_in_stock ? 'Disponible' : 'Sin stock'} en CKING`;

  const descripcion =
    `${producto.description}. Código ${producto.code}` +
    (rubro ? `, rubro ${rubro}` : '') +
    (producto.brand ? `, marca ${producto.brand}` : '') +
    `. ${disponibilidad}, repuestos para motos al por mayor en Argentina.`;

  // La foto de la pieza cuando alguien manda el link por WhatsApp, que es como se manda. Sin
  // sesión no va: nginx no sirve la foto sin cookie, y WhatsApp no la tiene.
  const foto = data.anonima ? undefined : data.fotos[0];

  // Cada ficha con su propio título y su propia descripción. Antes las 2.831 compartían las del
  // sitio entero, así que para un buscador eran 2.831 páginas iguales.
  return [
    { title: titulo },
    { name: 'description', content: descripcion },
    {
      tagName: 'link',
      rel: 'canonical',
      href: `${SITIO}/catalogo/${encodeURIComponent(codigo)}`,
    },
    { property: 'og:title', content: titulo },
    { property: 'og:description', content: descripcion },
    { property: 'og:type', content: 'product' },
    ...(foto ? [{ property: 'og:image', content: foto }] : []),
  ];
}

/**
 * Busca un producto por su código exacto, entre los que la API devuelve para esa búsqueda.
 *
 * `get` y no `getOr`: con una lista vacía de reserva, una API que no contesta terminaba en el
 * 404, y un 404 le dice a Google que el producto se dio de baja. Un corte tiene que verse como un
 * corte. El fallo sale tal cual: el loader distingue el 401 —no hay sesión, va la ficha pelada—
 * de lo demás.
 */
async function porCodigo(codigo: string, request: Request): Promise<Producto | null> {
  const { products } = await get<Respuesta>(RUTAS.catalogo.productos, {
    request,
    query: { search: codigo, limit: 50 },
  });

  const buscado = codigo.trim().toUpperCase();
  return products.find(p => p.code.trim().toUpperCase() === buscado) ?? null;
}

/** Lo mismo sin sesión, contra el resumen público. Un 404 es que no está, no un error. */
async function resumenPorCodigo(
  codigo: string,
  request: Request,
): Promise<ResumenDeProducto | null> {
  try {
    const { product } = await get<{ product: ResumenDeProducto }>(
      RUTAS.catalogo.resumenDelProducto(codigo),
      { request },
    );

    return product;
  } catch (fallo) {
    if (fallo instanceof ApiError && fallo.status === 404) return null;
    throw fallo;
  }
}

/**
 * Las direcciones viejas del sitemap.
 *
 * El script anterior publicaba `/catalogo/tapa-de-cilindro-CK-1234`: la descripción en guiones y
 * el código al final. La ficha nunca supo leerlas —comparaba el segmento entero contra el
 * código— así que las 2.831 direcciones entregadas a Google mostraban "Producto no encontrado".
 * Acá se recupera el código probando los finales del segmento, del más corto al más largo, y se
 * redirige a la dirección buena. Con el código pelado no entra nunca a esta función.
 */
async function porDireccionVieja<T>(
  segmento: string,
  buscar: (codigo: string) => Promise<T | null>,
): Promise<T | null> {
  const partes = segmento.split('-');

  for (let desde = partes.length - 1; desde > 0; desde--) {
    const candidato = partes.slice(desde).join('-');
    if (candidato.length < 2) continue;

    const producto = await buscar(candidato);
    if (producto) return producto;
  }

  return null;
}

/**
 * El producto de la dirección, con o sin sesión: lo que cambia es con qué se lo busca.
 *
 * Una dirección vieja redirige a la buena y una que no existe es un 404, en los dos casos: un
 * enlace viejo mandado por WhatsApp tiene que andar aunque quien lo abre no haya iniciado sesión.
 */
async function identificar<T extends { code: string }>(
  segmento: string,
  buscar: (codigo: string) => Promise<T | null>,
): Promise<T> {
  const producto = await buscar(segmento);
  if (producto) return producto;

  const viejo = await porDireccionVieja(segmento, buscar);

  if (viejo) {
    // 301 y no 302: la dirección buena es la otra y conviene que el buscador se quede con esa.
    throw redirect(`/catalogo/${encodeURIComponent(viejo.code)}`, 301);
  }

  throw new Response('Producto no encontrado', { status: 404 });
}

/** La redirección y el 404 salen como están; un fallo de la API, con su estado. */
function comoSalida(fallo: unknown): Response {
  return fallo instanceof Response ? fallo : comoRespuesta(fallo);
}

export async function loader({ params, request }: Route.LoaderArgs): Promise<DatosDeLaFicha> {
  const segmento = decodeURIComponent(params.codigo);

  let producto: Producto;

  try {
    producto = await identificar(segmento, codigo => porCodigo(codigo, request));
  } catch (fallo) {
    // Sin sesión la lista de productos contesta 401. No es un error: es la ficha pelada.
    if (!(fallo instanceof ApiError && fallo.isUnauthorized)) throw comoSalida(fallo);

    try {
      return {
        anonima: true,
        producto: await identificar(segmento, codigo => resumenPorCodigo(codigo, request)),
      };
    } catch (falloSinSesion) {
      throw comoSalida(falloSinSesion);
    }
  }

  const [fotosDelProducto, delMismoRubro] = await Promise.all([
    getOr<{ images: Imagen[] }>(
      RUTAS.catalogo.fotosDelProducto(producto.code),
      { images: [] },
      { request },
    ),

    producto.category
      ? getOr<Respuesta>(
          RUTAS.catalogo.productos,
          { products: [] },
          { request, query: { category: producto.category, limit: 60 } },
        )
      : Promise.resolve({ products: [] as Producto[] }),
  ]);

  // Seis del mismo rubro, salteados para que no sean siempre los mismos seis. El sorteo pasa
  // acá y no en el navegador: hecho al dibujar, el servidor y el navegador elegirían distinto y
  // React lo vería como un error.
  const candidatos = delMismoRubro.products.filter(p => p.code !== producto.code);
  const relacionados: Producto[] = [];
  const usados = new Set<number>();

  while (relacionados.length < RELACIONADOS && usados.size < candidatos.length) {
    const indice = Math.floor(Math.random() * candidatos.length);
    if (usados.has(indice)) continue;

    usados.add(indice);
    const elegido = candidatos[indice];
    if (elegido) relacionados.push(elegido);
  }

  const fotosDeRelacionados = Object.fromEntries(
    await Promise.all(
      relacionados.map(async relacionado => {
        const { images } = await getOr<{ images: Imagen[] }>(
          RUTAS.catalogo.fotosDelProducto(relacionado.code),
          { images: [] },
          { request },
        );

        return [relacionado.code, images[0]?.url ?? null] as const;
      }),
    ),
  ) as Record<string, string | null>;

  return {
    anonima: false,
    producto,
    fotos: fotosDelProducto.images.slice(0, MAXIMO_DE_FOTOS).map(imagen => imagen.url),
    relacionados,
    fotosDeRelacionados,
  };
}

export default function Ficha({ loaderData }: Route.ComponentProps) {
  if (loaderData.anonima) return <FichaPelada producto={loaderData.producto} />;

  return <FichaCompleta {...loaderData} />;
}

function FichaCompleta({
  producto,
  fotos,
  relacionados,
  fotosDeRelacionados,
}: Readonly<Omit<FichaConSesion, 'anonima'>>) {
  const { sesion } = useDatosDelSitio();
  const navigate = useNavigate();

  const conPrecios = sesion !== null;

  return (
    <Box sx={{ py: 4, bgcolor: '#121212', minHeight: '100vh' }}>
      <MedirProducto code={producto.code} brand={producto.brand} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            comoProducto(
              producto,
              fotos[0] ?? null,
              tieneRubro(producto.category) ? producto.category : null,
            ),
            migasDePan([
              ...(tieneRubro(producto.category)
                ? [
                    { nombre: 'Rubros', ruta: '/rubros' },
                    {
                      nombre: producto.category,
                      ruta: direccionDelRubro(producto.category) ?? '/rubros',
                    },
                  ]
                : [{ nombre: 'Catálogo', ruta: '/catalogo' }]),
              { nombre: producto.code, ruta: `/catalogo/${encodeURIComponent(producto.code)}` },
            ]),
          ]),
        }}
      />

      <Container maxWidth="xl">
        <Box
          component="nav"
          aria-label="Breadcrumb"
          sx={{
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            fontSize: 14,
            color: 'text.secondary',
          }}
        >
          <Link to="/" style={{ color: 'inherit', fontWeight: 500 }}>
            <Box component="span" sx={{ '&:hover, &:focus-visible': { color: '#3483fa' } }}>
              Inicio
            </Box>
          </Link>

          <Typography component="span" aria-hidden="true" sx={{ color: 'text.secondary', mx: 1 }}>
            {' › '}
          </Typography>

          <Link
            to={direccionDelRubro(producto.category) ?? '/catalogo'}
            style={{ color: 'inherit', fontWeight: 500 }}
          >
            <Box component="span" sx={{ '&:hover, &:focus-visible': { color: '#3483fa' } }}>
              {tieneRubro(producto.category) ? producto.category : 'Catálogo'}
            </Box>
          </Link>

          <Typography component="span" aria-hidden="true" sx={{ color: 'text.secondary', mx: 1 }}>
            {' › '}
          </Typography>

          <Typography
            component="span"
            aria-current="page"
            sx={{ color: 'text.primary', ml: 1, fontWeight: 600 }}
          >
            {producto.code}
          </Typography>
        </Box>

        <Button startIcon={<ArrowBack />} onClick={() => void navigate(-1)}>
          Volver
        </Button>

        <Box sx={{ width: '100%', px: { xs: 2, sm: 4 }, py: 2 }}>
          <Box sx={{ mb: 4 }}>
            <FichaDelProducto producto={producto} conPrecios={conPrecios} />
          </Box>

          <Box sx={{ mt: 1, mb: 2 }}>
            <GaleriaDeProducto fotos={fotos} />
          </Box>
        </Box>

        {relacionados.length > 0 && (
          <Box sx={{ mt: 8 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 4, fontWeight: 600 }}>
              Productos relacionados
            </Typography>

            <Grid container spacing={3}>
              {relacionados.map(relacionado => (
                <Grid key={relacionado.code} size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
                  <TarjetaRelacionada
                    relacionado={relacionado}
                    foto={fotosDeRelacionados[relacionado.code] ?? null}
                    conPrecios={conPrecios}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
}

/**
 * La ficha sin sesión: el código, el nombre, la marca y el rubro, en texto.
 *
 * Es lo que lee Google y lo que abre quien recibió el enlace por WhatsApp sin haber iniciado
 * sesión. El botón lleva al ingreso y de vuelta acá, ya con fotos, stock y precios.
 *
 * Las migas no enlazan al rubro: la página del rubro pide sesión, y un buscador que siguiera el
 * enlace terminaría en la pantalla de ingreso.
 */
function FichaPelada({ producto }: Readonly<{ producto: ResumenDeProducto }>) {
  const navigate = useNavigate();

  const direccion = `/catalogo/${encodeURIComponent(producto.code)}`;
  const rubro = tieneRubro(producto.category) ? producto.category : null;

  return (
    <Box sx={{ py: 4, bgcolor: '#121212', minHeight: '100vh' }}>
      <MedirProducto code={producto.code} brand={producto.brand} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            comoProducto(producto, null, rubro),
            migasDePan([{ nombre: producto.code, ruta: direccion }]),
          ]),
        }}
      />

      <Container maxWidth="md">
        <Box
          component="nav"
          aria-label="Breadcrumb"
          sx={{
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            fontSize: 14,
            color: 'text.secondary',
          }}
        >
          <Link to="/" style={{ color: 'inherit', fontWeight: 500 }}>
            <Box component="span" sx={{ '&:hover, &:focus-visible': { color: '#3483fa' } }}>
              Inicio
            </Box>
          </Link>

          <Typography component="span" aria-hidden="true" sx={{ color: 'text.secondary', mx: 1 }}>
            {' › '}
          </Typography>

          <Typography
            component="span"
            aria-current="page"
            sx={{ color: 'text.primary', ml: 1, fontWeight: 600 }}
          >
            {producto.code}
          </Typography>
        </Box>

        <Button startIcon={<ArrowBack />} onClick={() => void navigate(-1)}>
          Volver
        </Button>

        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: { xs: 2, sm: 4 },
            bgcolor: '#1e1e1e',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {rubro && (
            <Box>
              <Chip label={rubro} color="primary" size="small" />
            </Box>
          )}

          {/* El mismo h1 que la ficha completa: código y descripción, con un guion que no se ve
              para que el texto del título no quede "ACS001CARGADOR USB". */}
          <Box
            component="h1"
            sx={{
              m: 0,
              fontSize: 'inherit',
              fontWeight: 400,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <span
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clipPath: 'inset(50%)',
              }}
            >
              {' — '}
            </span>

            <CodigoDePieza codigo={producto.code} variante="display" />

            <Box sx={{ width: 48, height: '3px', bgcolor: 'error.main' }} />

            <Typography component="span" variant="body1" sx={{ maxWidth: '62ch' }}>
              {producto.description}
            </Typography>
          </Box>

          {producto.brand && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Marca{' '}
              <Box component="strong" sx={{ color: 'text.primary' }}>
                {nombreDeMarca(producto.brand)}
              </Box>
            </Typography>
          )}

          <Box>
            <Button
              component={Link}
              to={direccionDeIngreso(direccion)}
              variant="contained"
              color="primary"
              startIcon={<Lock />}
            >
              Iniciá sesión para ver fotos, stock y precios
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

/**
 * Una de las tarjetas de "Productos relacionados".
 *
 * Está afuera del `map` porque necesita `useViewTransitionState`, y un hook no puede llamarse
 * dentro de un bucle. Sin esto las seis tarjetas llevarían el mismo nombre de transición y el
 * navegador descartaría la animación.
 */
function TarjetaRelacionada({
  relacionado,
  foto,
  conPrecios,
}: {
  relacionado: Producto;
  foto: string | null;
  conPrecios: boolean;
}) {
  const ficha = `/catalogo/${encodeURIComponent(relacionado.code)}`;
  const viajando = useViewTransitionState(ficha);

  return (
    <Link to={ficha} viewTransition style={{ textDecoration: 'none', color: 'inherit' }}>
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          bgcolor: '#1e1e1e',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: 140,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Blanco, igual que el recuadro de las tarjetas del catálogo y el de la foto grande
            // de arriba. Las fotos vienen con fondo blanco: sobre negro se veía el recorte del
            // archivo antes que el producto.
            bgcolor: '#fff',
            p: 1,
          }}
        >
          {foto ? (
            <Box
              component="img"
              src={foto}
              alt={relacionado.description}
              loading="lazy"
              sizes="(max-width: 600px) 92vw, (max-width: 900px) 46vw, 200px"
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                viewTransitionName: viajando ? 'foto-producto' : 'none',
              }}
            />
          ) : (
            <Typography
              variant="caption"
              // `text.secondary` es un gris claro pensado para el fondo oscuro: sobre el
              // recuadro blanco quedaba ilegible. Mismo gris que usa la tarjeta del catálogo.
              sx={{ color: 'grey.500', textAlign: 'center' }}
            >
              Imagen no disponible
            </Typography>
          )}
        </Box>

        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#252525' }}>
          <CodigoDePieza
            codigo={relacionado.code}
            sx={{ viewTransitionName: viajando ? 'codigo-producto' : 'none' }}
          />
        </Box>

        <Box sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, height: 60, overflow: 'hidden' }}>
            {relacionado.description}
          </Typography>

          {conPrecios && (
            <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 'bold', mt: 1 }}>
              {relacionado.price === null ? 'Sin precio' : enPesos(relacionado.price)}
            </Typography>
          )}
        </Box>
      </Paper>
    </Link>
  );
}

/** Un producto que no está deja la pantalla de "no encontrado", no una página en blanco. */
export function ErrorBoundary() {
  return (
    <Container sx={{ py: { xs: 8, md: 12 }, textAlign: 'center', maxWidth: 640 }}>
      <Typography variant="h2" component="h1" gutterBottom>
        No encontramos esa pieza
      </Typography>

      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Puede que el enlace esté viejo o que el producto ya no esté en el catálogo activo.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button component={Link} to="/catalogo" variant="contained" color="primary">
          Buscar en el catálogo
        </Button>

        <Button component={Link} to="/rubros" variant="outlined" color="primary">
          Ver los rubros
        </Button>
      </Box>
    </Container>
  );
}
